import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Car, X, ChevronRight, Gauge } from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'
import { useVehicles, useAddVehicle } from '../api/hooks'

// ─── Registration plate display ───────────────────────────────────────────────

function Plate({ registration }) {
  return (
    <span className="inline-flex items-center gap-1.5 font-mono font-bold tracking-widest text-sm
                     bg-yellow-400 text-black px-3 py-1 rounded select-all">
      {registration}
    </span>
  )
}

// ─── Fuel type badge ──────────────────────────────────────────────────────────

const FUEL_STYLES = {
  petrol:   'bg-blue-500/15 text-blue-400',
  diesel:   'bg-slate-500/20 text-slate-400',
  electric: 'bg-green-500/15 text-green-400',
  hybrid:   'bg-teal-500/15 text-teal-400',
  other:    'bg-gray-500/15 text-gray-400',
}

function FuelBadge({ type }) {
  if (!type) return null
  return (
    <span className={clsx('px-2 py-0.5 rounded text-xs font-medium capitalize', FUEL_STYLES[type] || FUEL_STYLES.other)}>
      {type}
    </span>
  )
}

// ─── Vehicle card ─────────────────────────────────────────────────────────────

function VehicleCard({ vehicle }) {
  const navigate = useNavigate()
  return (
    <button
      onClick={() => navigate(`/vehicles/${vehicle.id}`)}
      className="w-full text-left bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5
                 hover:border-[var(--border-strong)] hover:shadow-card-hover transition-all group"
    >
      <Plate registration={vehicle.registration} />

      <p className="mt-3 font-display font-bold text-[var(--text-primary)] group-hover:text-indigo-400 transition-colors">
        {vehicle.make} {vehicle.model}
      </p>
      <p className="text-sm text-[var(--text-secondary)]">
        {vehicle.year ? vehicle.year : 'Year unknown'}
        {vehicle.colour ? ` · ${vehicle.colour}` : ''}
      </p>

      <div className="flex items-center gap-2 mt-3">
        <FuelBadge type={vehicle.fuel_type} />
        {vehicle.transmission && (
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-[var(--bg-overlay)] text-[var(--text-muted)] capitalize">
            {vehicle.transmission}
          </span>
        )}
      </div>

      {vehicle.current_mileage != null && (
        <div className="flex items-center gap-1.5 mt-4 text-xs text-[var(--text-muted)]">
          <Gauge size={12} />
          {Number(vehicle.current_mileage).toLocaleString()} miles
        </div>
      )}

      <div className="flex items-center justify-end mt-4">
        <ChevronRight size={16} className="text-[var(--text-muted)] group-hover:text-indigo-400 transition-colors" />
      </div>
    </button>
  )
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({ onAdd, isCustomer }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-16 h-16 rounded-2xl bg-[var(--bg-surface)] border border-[var(--border)] flex items-center justify-center mb-4">
        <Car size={28} className="text-[var(--text-muted)]" />
      </div>
      <h3 className="font-display font-bold text-lg text-[var(--text-primary)] mb-2">No vehicles yet</h3>
      {isCustomer ? (
        <>
          <p className="text-sm text-[var(--text-secondary)] mb-6 max-w-xs">
            Add your first vehicle to start tracking service history, upcoming maintenance, and issues.
          </p>
          <button
            onClick={onAdd}
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700
                       text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus size={16} />
            Add your first vehicle
          </button>
        </>
      ) : (
        <p className="text-sm text-[var(--text-secondary)] max-w-xs">
          Vehicles will appear here once they are linked to your garage.
        </p>
      )}
    </div>
  )
}

// ─── Add Vehicle modal ────────────────────────────────────────────────────────

const FUEL_OPTIONS = ['petrol', 'diesel', 'electric', 'hybrid', 'other']
const TRANS_OPTIONS = ['manual', 'automatic', 'other']

const EMPTY_FORM = {
  registration: '', make: '', model: '', year: '',
  colour: '', fuelType: '', transmission: '',
  engineSize: '', vin: '', currentMileage: '',
}

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
        {label}
        {hint && <span className="ml-1 font-normal text-[var(--text-muted)]">{hint}</span>}
      </label>
      {children}
    </div>
  )
}

const inputCls = `w-full px-3 py-2 text-sm bg-[var(--bg-base)] border border-[var(--border)]
  rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
  focus:outline-none focus:border-indigo-500 transition-colors`

const selectCls = `${inputCls} cursor-pointer`

function AddVehicleModal({ onClose }) {
  const addVehicle = useAddVehicle()
  const [form, setForm] = useState(EMPTY_FORM)

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      await addVehicle.mutateAsync({
        registration:  form.registration,
        make:          form.make,
        model:         form.model,
        year:          form.year          ? parseInt(form.year)          : null,
        colour:        form.colour        || null,
        fuelType:      form.fuelType      || null,
        transmission:  form.transmission  || null,
        engineSize:    form.engineSize    || null,
        vin:           form.vin           || null,
        currentMileage:form.currentMileage? parseInt(form.currentMileage): null,
      })
      toast.success('Vehicle added')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add vehicle')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-lg bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">Add Vehicle</h2>
          <button onClick={onClose} className="p-1 rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[70vh]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Registration" hint="(required)">
              <input type="text" placeholder="AB12CDE" value={form.registration} onChange={set('registration')}
                required className={inputCls} style={{ textTransform: 'uppercase' }} />
            </Field>

            <Field label="Make" hint="(required)">
              <input type="text" placeholder="Ford" value={form.make} onChange={set('make')}
                required className={inputCls} />
            </Field>

            <Field label="Model" hint="(required)">
              <input type="text" placeholder="Focus" value={form.model} onChange={set('model')}
                required className={inputCls} />
            </Field>

            <Field label="Year">
              <input type="number" placeholder="2019" min="1900" max={new Date().getFullYear() + 1}
                value={form.year} onChange={set('year')} className={inputCls} />
            </Field>

            <Field label="Colour">
              <input type="text" placeholder="Silver" value={form.colour} onChange={set('colour')}
                className={inputCls} />
            </Field>

            <Field label="Fuel type">
              <select value={form.fuelType} onChange={set('fuelType')} className={selectCls}>
                <option value="">Select…</option>
                {FUEL_OPTIONS.map(f => (
                  <option key={f} value={f} className="capitalize">{f.charAt(0).toUpperCase() + f.slice(1)}</option>
                ))}
              </select>
            </Field>

            <Field label="Transmission">
              <select value={form.transmission} onChange={set('transmission')} className={selectCls}>
                <option value="">Select…</option>
                {TRANS_OPTIONS.map(t => (
                  <option key={t} value={t} className="capitalize">{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </select>
            </Field>

            <Field label="Engine size" hint="e.g. 1.6">
              <input type="text" placeholder="1.6" value={form.engineSize} onChange={set('engineSize')}
                className={inputCls} />
            </Field>

            <Field label="Current mileage">
              <input type="number" placeholder="45000" min="0" value={form.currentMileage}
                onChange={set('currentMileage')} className={inputCls} />
            </Field>

            <Field label="VIN" hint="optional">
              <input type="text" placeholder="WBA…" value={form.vin} onChange={set('vin')}
                className={inputCls} />
            </Field>
          </div>

          <div className="flex gap-3 pt-6 mt-2 border-t border-[var(--border)]">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={addVehicle.isPending}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                         text-white text-sm font-medium rounded-lg transition-colors">
              {addVehicle.isPending ? 'Adding…' : 'Add Vehicle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function VehiclesPage() {
  const { user } = useAuth()
  const { data: vehicles = [], isLoading } = useVehicles()
  const [showAdd, setShowAdd] = useState(false)

  const isCustomer = user?.role === 'customer'

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">Vehicles</h1>
          {!isLoading && vehicles.length > 0 && (
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              {vehicles.length} {vehicles.length === 1 ? 'vehicle' : 'vehicles'}
            </p>
          )}
        </div>
        {isCustomer && (
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700
                       text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus size={16} />
            Add Vehicle
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-52 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl animate-pulse" />
          ))}
        </div>
      ) : vehicles.length === 0 ? (
        <EmptyState onAdd={() => setShowAdd(true)} isCustomer={isCustomer} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {vehicles.map(v => (
            <VehicleCard key={v.id} vehicle={v} />
          ))}
        </div>
      )}

      {showAdd && <AddVehicleModal onClose={() => setShowAdd(false)} />}
    </div>
  )
}
